export interface RequiredProductEntry {
    category: string;
    manufacturerProductId: string;
    productName: string;
    formulaProductName?: string;
}

export interface ParentProductMapping {
    parentManufacturerProductId: string;
    parentProductName: string;
    requiredProducts: RequiredProductEntry[];
}

export interface ProductDependencyRule {
    category: string;
    /** Categories that must be selected before this category. */
    prerequisiteCategories: string[];
    mappings: ParentProductMapping[];
}

export interface ProductDependenciesConfig {
    version: number;
    rules: ProductDependencyRule[];
}

/** Legacy shape used by schema builder and selection handlers. */
export interface LegacyDependencyRule {
    Category: string;
    PrerequisiteCategories: string[];
    Mappings: Array<{
        Product: string;
        parentManufacturerProductId?: string;
        RequiredProducts: Array<{
            Category: string;
            Product: string;
            manufacturerProductId: string;
            formulaProductName?: string;
        }>;
    }>;
}

type LegacyProductDependencyRule = ProductDependencyRule & { requiresCategories?: string[] };

function normalizeDependencyRule(rule: LegacyProductDependencyRule): ProductDependencyRule {
    if (rule.prerequisiteCategories) {
        return rule;
    }

    const mappedCategories = new Set(
        rule.mappings.flatMap((mapping) => mapping.requiredProducts.map((product) => product.category)),
    );
    const legacyRequires = rule.requiresCategories ?? [];

    return {
        ...rule,
        prerequisiteCategories: legacyRequires.filter((category) => !mappedCategories.has(category)),
    };
}

export function toLegacyDependencyRules(config: ProductDependenciesConfig): LegacyDependencyRule[] {
    return config.rules.map((rule) => {
        const normalized = normalizeDependencyRule(rule as LegacyProductDependencyRule);
        return {
            Category: normalized.category,
            PrerequisiteCategories: normalized.prerequisiteCategories ?? [],
            Mappings: normalized.mappings.map((mapping) => ({
                Product: mapping.parentProductName,
                parentManufacturerProductId: mapping.parentManufacturerProductId,
                RequiredProducts: mapping.requiredProducts.map((req) => ({
                    Category: req.category,
                    Product: req.productName,
                    manufacturerProductId: req.manufacturerProductId,
                    formulaProductName: req.formulaProductName,
                })),
            })),
        };
    });
}
